import { module as BaseModule } from "modujs"
import { updateCartCountDisplay } from "../utils/cart"

export default class extends BaseModule {
    init() {
        this.ajaxUrl = this.el.dataset.ajaxUrl || (typeof window !== "undefined" ? window.ajaxurl : null)
        this.cartItems = this.el.querySelectorAll("[data-cart-item]")
        this.cartTotalPrice = this.el.querySelector("[data-cart-total-price]")
        this.cartContainer = this.el.querySelector("[data-cart-container]")

        this.handleQuantityChange = this.handleQuantityChange.bind(this)
        this.handleQuantityInput = this.handleQuantityInput.bind(this)
        this.handleRemove = this.handleRemove.bind(this)

        if (!this.ajaxUrl) {
            console.error("Cart: AJAX URL not found")
            return
        }

        this.cartItems.forEach((item) => {
            const decreaseBtn = item.querySelector("[data-cart-qty-decrease]")
            const increaseBtn = item.querySelector("[data-cart-qty-increase]")
            const qtyInput = item.querySelector("[data-cart-qty-input]")
            const removeBtn = item.querySelector("[data-cart-remove]")

            if (decreaseBtn) {
                decreaseBtn.addEventListener("click", this.handleQuantityChange)
            }
            if (increaseBtn) {
                increaseBtn.addEventListener("click", this.handleQuantityChange)
            }
            if (qtyInput) {
                qtyInput.addEventListener("change", this.handleQuantityInput)
                qtyInput.addEventListener("blur", this.handleQuantityInput)
            }
            if (removeBtn) {
                removeBtn.addEventListener("click", this.handleRemove)
            }
        })
    }

    async handleQuantityChange(event) {
        const button = event.currentTarget
        const cartItem = button.closest("[data-cart-item]")
        const qtyInput = cartItem.querySelector("[data-cart-qty-input]")
        const cartItemKey = cartItem.dataset.cartItemKey

        if (!qtyInput || !cartItemKey) {
            return
        }

        const currentQty = parseInt(qtyInput.value, 10) || 0
        const isDecrease = button.dataset.cartQtyDecrease !== undefined
        const newQty = isDecrease ? Math.max(0, currentQty - 1) : currentQty + 1

        qtyInput.value = newQty

        await this.updateCartItem(cartItemKey, newQty, cartItem)
    }

    async handleQuantityInput(event) {
        const input = event.currentTarget
        const cartItem = input.closest("[data-cart-item]")
        const cartItemKey = cartItem.dataset.cartItemKey

        if (!cartItemKey) {
            return
        }

        const newQty = parseInt(input.value, 10) || 0

        await this.updateCartItem(cartItemKey, newQty, cartItem)
    }

    async handleRemove(event) {
        const button = event.currentTarget
        const cartItem = button.closest("[data-cart-item]")
        const cartItemKey = cartItem.dataset.cartItemKey

        if (!cartItemKey) {
            return
        }

        await this.removeCartItem(cartItemKey, cartItem)
    }

    async updateCartItem(cartItemKey, quantity, cartItemElement) {
        if (!this.ajaxUrl || !cartItemKey) {
            return
        }

        const qtyInput = cartItemElement.querySelector("[data-cart-qty-input]")
        const priceElement = cartItemElement.querySelector("[data-cart-item-price]")
        const isUpdating = cartItemElement.dataset.updating === "true"

        if (isUpdating) {
            return
        }

        cartItemElement.dataset.updating = "true"
        if (qtyInput) {
            qtyInput.disabled = true
        }

        try {
            const payload = new URLSearchParams()
            payload.append("action", "lc_update_cart_item")
            payload.append("cart_item_key", cartItemKey)
            payload.append("quantity", quantity)

            const response = await fetch(this.ajaxUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
                body: payload.toString(),
            })

            const result = await response.json()

            if (!response.ok || !result || !result.success) {
                const errorMessage = result?.data?.message || "Impossible de mettre à jour le panier."
                throw new Error(errorMessage)
            }

            // Update cart count
            if (result?.data?.cart_count !== undefined) {
                updateCartCountDisplay(result.data.cart_count)

                window.dispatchEvent(
                    new CustomEvent("cart:updated", {
                        detail: {
                            count: result.data.cart_count,
                        },
                    })
                )
            }

            // Update total
            if (result?.data?.cart_total_formatted && this.cartTotalPrice) {
                this.cartTotalPrice.innerHTML = result.data.cart_total_formatted
            }

            // If quantity is 0 or cart is empty, remove the item from DOM
            if (quantity <= 0 || result?.data?.cart_empty) {
                cartItemElement.remove()
                
                // Check if cart is now empty
                if (result?.data?.cart_empty) {
                    this.showEmptyCartMessage()
                }
                return
            }

            // Update item price if available
            if (result?.data?.cart_item && priceElement) {
                const item = result.data.cart_item
                if (item.is_on_sale) {
                    priceElement.innerHTML = `
                        <span class="mr-2 line-through opacity-70">${item.regular_price_formatted}</span>
                        <span class="text-accent">${item.sale_price_formatted}</span>
                    `
                } else {
                    priceElement.innerHTML = item.line_total_formatted
                }
            }

            // Update quantity input value
            if (qtyInput && result?.data?.cart_item) {
                qtyInput.value = result.data.cart_item.quantity
            }
        } catch (error) {
            console.error("Error updating cart item:", error)
            // Revert quantity input on error
            if (qtyInput) {
                const currentCartItem = this.el.querySelector(`[data-cart-item-key="${cartItemKey}"]`)
                if (currentCartItem) {
                    const currentQtyInput = currentCartItem.querySelector("[data-cart-qty-input]")
                    if (currentQtyInput) {
                        // Reload page to sync with server state
                        window.location.reload()
                    }
                }
            }
        } finally {
            cartItemElement.dataset.updating = "false"
            if (qtyInput) {
                qtyInput.disabled = false
            }
        }
    }

    async removeCartItem(cartItemKey, cartItemElement) {
        if (!this.ajaxUrl || !cartItemKey) {
            return
        }

        const isRemoving = cartItemElement.dataset.removing === "true"

        if (isRemoving) {
            return
        }

        cartItemElement.dataset.removing = "true"

        try {
            const payload = new URLSearchParams()
            payload.append("action", "lc_remove_cart_item")
            payload.append("cart_item_key", cartItemKey)

            const response = await fetch(this.ajaxUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
                body: payload.toString(),
            })

            const result = await response.json()

            if (!response.ok || !result || !result.success) {
                const errorMessage = result?.data?.message || "Impossible de supprimer l'article du panier."
                throw new Error(errorMessage)
            }

            // Update cart count
            if (result?.data?.cart_count !== undefined) {
                updateCartCountDisplay(result.data.cart_count)

                window.dispatchEvent(
                    new CustomEvent("cart:updated", {
                        detail: {
                            count: result.data.cart_count,
                        },
                    })
                )
            }

            // Update total
            if (result?.data?.cart_total_formatted && this.cartTotalPrice) {
                this.cartTotalPrice.innerHTML = result.data.cart_total_formatted
            }

            // Remove item from DOM
            cartItemElement.remove()

            // Check if cart is now empty
            if (result?.data?.cart_empty) {
                this.showEmptyCartMessage()
            }
        } catch (error) {
            console.error("Error removing cart item:", error)
            // Reload page to sync with server state
            window.location.reload()
        } finally {
            cartItemElement.dataset.removing = "false"
        }
    }

    showEmptyCartMessage() {
        const cartItemsContainer = this.el.querySelector(".grid.gap-2")
        if (cartItemsContainer) {
            const existingMessage = cartItemsContainer.querySelector("[data-empty-message]")
            if (!existingMessage) {
                const emptyMessage = document.createElement("p")
                emptyMessage.className = "style-24px"
                emptyMessage.setAttribute("data-empty-message", "")
                emptyMessage.textContent = document.documentElement.lang === "fr" 
                    ? "Votre panier est vide." 
                    : "Your cart is empty."
                cartItemsContainer.insertBefore(emptyMessage, cartItemsContainer.firstChild.nextSibling)
            }
        }
    }
}

